/**
 * Adding a Canonical Position Group, from the dropdown that offers them.
 *
 * The client asked to be able to add options to *Positions Groups Excluded*.
 * The naive form of that — a text box taking a group name — builds a
 * declaration that can never fire: a player's group is whatever `canonicalise`
 * reads out of a Source Profile's `position_map`, so a group no label maps to
 * has no members, and a statistic excluding it excludes nobody for ever with
 * nothing raising.
 *
 * So this dialog asks for the group **and the publisher's own labels that feed
 * it**, and writes the profile's map. The group then appears in the dropdown
 * because players are in it — `groups_for_sport` reads the values of every
 * profile's map — which is what keeps the declaration and the cohort from being
 * able to disagree.
 *
 * **The labels are offered first, and typed only as an exception.** They come
 * from `/source-profiles/{id}/position-labels`, which is what the source
 * actually published on its latest Run, with a count and the group each
 * currently lands in. The ones reading `UNKNOWN` are the ones nothing maps —
 * the reason anybody opens this — so they are listed first. Typing a
 * publisher's vocabulary from memory into the column that decides which cohort
 * a player is ranked in is the thing this exists to prevent, and the offered
 * list is how it prevents it.
 *
 * It is an exception rather than a refusal because a mapping for a label the
 * latest Run did not publish is legitimate, which `playersMoved` already said:
 * a source may not have run yet, a publisher may send the label next season,
 * and a cohort that only appears in a postseason file still has to be declared
 * before the file arrives. So a label can be added by hand — refused where the
 * source already publishes it, so the measured row is ticked rather than copied
 * — and the ones that were somebody's word are marked as such in the list and
 * counted as moving nobody.
 *
 * **It is scoped to one Source Profile, and says so.** A `position_map` belongs
 * to a profile, not to a sport: two sources in one sport can spell a position
 * differently and only one of them may send `ST`. A dialog that hid that would
 * be writing to a profile the operator never chose.
 *
 * The arithmetic — and the one trap, that the server *replaces* the map rather
 * than merging it — is `src/position-map.ts`, which holds no React so `tests/`
 * can assert it.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, api, type SourceProfile } from "../api";
import { keys } from "../queries";
import {
  labelsFromPayload,
  merged,
  needingAttentionFirst,
  normaliseGroup,
  playersMoved,
  reassigned,
  typedLabel,
  type PositionLabel,
} from "../position-map";
import {
  Badge,
  Banner,
  Button,
  Checkbox,
  Field,
  FieldSet,
  Modal,
  Muted,
  Select,
  Spinner,
  TextInput,
} from "./ui";

/**
 * The source being configured, where it has not been saved yet.
 *
 * **Add Source's shape of this dialog.** There is no profile to write to and no
 * published Run to read labels from — but discovery has just fetched the
 * payload, so the labels are in hand: the distinct values of whichever field
 * the reviewer bound as Position. The mapping goes into the draft the screen
 * will create the profile with, in the same request as the field review.
 */
export interface DraftSource {
  /** The field bound as Position, and the values it takes in the payload. */
  labels: readonly { value: string; records: number }[];
  /** The map the profile will be created with, composed by its Source Template
   *  where one composed it. */
  positionMap: Readonly<Record<string, string>>;
  onMapped: (map: Record<string, string>) => void;
}

export function AddPositionGroupModal({
  sport,
  draft,
  onClose,
  onAdded,
}: {
  sport: string;
  /**
   * Set only on Add Source, where the profile does not exist yet.
   *
   * It replaces both halves of the saved flow: the labels come from discovery
   * rather than from a Run, and the mapping is handed back to the screen rather
   * than written with a PATCH. Everything between — what a group is, what it
   * refuses, what it warns about — is the same code, which is the point of
   * threading one object through rather than writing the dialog twice.
   */
  draft?: DraftSource;
  onClose: () => void;
  /** The group that now exists, so the control that opened this can select it
   *  without waiting for a refetch to tell it the name. */
  onAdded: (group: string) => void;
}) {
  const queryClient = useQueryClient();
  const [profileId, setProfileId] = useState("");
  const [group, setGroup] = useState("");
  const [chosen, setChosen] = useState<string[]>([]);
  /** Labels added by hand, for a source that has not published them. Kept apart
   *  from `chosen` so the list can say which were measured and which were
   *  somebody's word, and so a label typed and then unticked is still offered. */
  const [typed, setTyped] = useState<string[]>([]);
  const [typing, setTyping] = useState("");
  const [problem, setProblem] = useState<{ field: string; message: string } | null>(null);

  const { data: profiles, isPending: profilesPending } = useQuery({
    queryKey: keys.profiles,
    queryFn: api.listProfiles,
    // Nothing to choose between on Add Source: the source is the one being
    // created, and the saved ones are other people's.
    enabled: !draft,
  });

  // This sport's profiles only. A football profile's map cannot create a soccer
  // group, and the server would refuse the exclusion that followed.
  const candidates = useMemo(
    () => (profiles ?? []).filter((profile: SourceProfile) => profile.sport === sport),
    [profiles, sport],
  );

  // Opened on the first candidate rather than on nothing, so the common case —
  // one source in the sport — needs no choice at all. Re-seeded if the list
  // arrives after the dialog.
  useEffect(() => {
    const first = candidates[0];
    if (profileId === "" && first) setProfileId(first.id);
  }, [candidates, profileId]);

  const profile = candidates.find((candidate) => candidate.id === profileId) ?? null;

  const { data: published, isPending: labelsPending } = useQuery({
    queryKey: keys.positionLabels(profileId),
    queryFn: () => api.listPositionLabels(profileId),
    enabled: !draft && profileId !== "",
  });

  // Changing the source changes which labels exist, so a selection made against
  // the old one is withdrawn rather than carried to a profile that may not send
  // those labels at all.
  useEffect(() => {
    setChosen([]);
    // Typed labels go with them: they were somebody's word about *that*
    // publisher's vocabulary, and carrying them to another source would be
    // asserting the same thing about a publisher nobody has looked at.
    setTyped([]);
    setTyping("");
    setProblem(null);
  }, [profileId]);

  /**
   * The labels to offer, from whichever record exists.
   *
   * A draft reads discovery's distinct values, which carry no group because
   * nothing has cohorted them yet — so they are shown against the group the
   * *existing* map would put them in, which for an unmapped label is `UNKNOWN`,
   * exactly as a Run would have recorded it. The rest of this component cannot
   * tell the two apart, which is why there is one dialog rather than two.
   */
  const available: PositionLabel[] = useMemo(
    () =>
      draft
        ? labelsFromPayload(draft.labels, draft.positionMap)
        : (published?.labels ?? []),
    [draft, published],
  );

  const rows = useMemo(() => needingAttentionFirst(available), [available]);
  const heldMap = draft ? draft.positionMap : (profile?.position_map ?? null);
  /**
   * The hand-added labels, in the shape the measured ones arrive in.
   *
   * Nought players, because that is true: the latest Run did not publish them.
   * `playersMoved` already counts them as nought, so the figure under the list
   * stays honest without this having to be special-cased there.
   */
  const typedRows = useMemo(
    () => labelsFromPayload(typed.map((value) => ({ value, records: 0 })), heldMap ?? {}),
    [typed, heldMap],
  );
  const moving = playersMoved(available, chosen);
  /**
   * Whether this source can produce a group at all.
   *
   * **Only the wait is disqualifying now.** This used to require a published
   * label, because with none to tick the button could only ever answer "choose
   * at least one" — a dead end that blamed the operator for an absence that was
   * not theirs. A label can now be added by hand, so the absence is something
   * they can work out of and the button is theirs to press. The refusal in
   * `merged` still catches an empty group, which is the case it was always for.
   */
  const usable = draft ? true : !labelsPending;
  const taken = heldMap ? reassigned(heldMap, chosen, group) : [];

  const save = useMutation({
    mutationFn: (map: Record<string, string>) =>
      api.updateProfile(profileId, { position_map: map }),
    onSuccess: async () => {
      // The dropdown reads the groups off every profile's map, and this screen
      // reads the labels off the Run — so both go stale on one save.
      await queryClient.invalidateQueries({ queryKey: keys.positionGroups });
      await queryClient.invalidateQueries({ queryKey: keys.profiles });
      await queryClient.invalidateQueries({ queryKey: keys.positionLabels(profileId) });
      onAdded(normaliseGroup(group));
    },
  });

  function submit() {
    if (!heldMap) {
      setProblem({
        field: "profile",
        message: "Choose the source whose labels feed this group.",
      });
      return;
    }
    const result = merged(heldMap, chosen, group);
    if (!result.ok) {
      setProblem({ field: result.field, message: result.message });
      return;
    }
    setProblem(null);
    if (draft) {
      // Handed to the screen rather than written. The profile does not exist,
      // so the map travels with the field review in the one request that
      // creates it — the same reason a Binding there names its Feed by label.
      draft.onMapped(result.map);
      onAdded(normaliseGroup(group));
      return;
    }
    save.mutate(result.map);
  }

  /**
   * Take the label in the box, or say why not.
   *
   * Ticked on arrival: somebody typing a label into this dialog is saying they
   * want it in the group, and making them tick a row they just created would be
   * asking the same question twice.
   */
  function addTyped() {
    const result = typedLabel(typing, available, typed);
    if (!result.ok) {
      setProblem({ field: "typed", message: result.message });
      return;
    }
    setTyped((held) => [...held, result.label]);
    setChosen((held) => [...held, result.label]);
    setTyping("");
    setProblem(null);
  }

  const errorFor = (field: string) => (problem?.field === field ? problem.message : null);
  const refusal =
    save.error instanceof ApiError
      ? save.error.message
      : save.error
        ? "Could not add that position group."
        : null;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Add a ${sport} position group`}
      description="A group is the publisher's own labels, collected under one name. The ranking compares a player against it, so a group no label feeds has nobody in it."
      width="max-w-[42rem]"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={save.isPending || !usable}
            onClick={submit}
          >
            {save.isPending ? "Adding…" : "Add group"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-md">
        {refusal && <Banner tone="danger" title={refusal} />}

        {!draft && !profilesPending && candidates.length === 0 && (
          // Honest rather than an empty form: with no source in the sport there
          // is no map to write to, and a group written anywhere else would be
          // one the dropdown never offers.
          <Banner tone="warning" title={`No ${sport} source is configured yet`}>
            A position group is made of a source's own labels, so there has to be
            a source first. Add one, run it once, and its labels will be here.
          </Banner>
        )}

        {(draft || candidates.length > 0) && (
          <>
            {/*
              Named rather than hidden even where there is only one, because
              what is being edited is *that source's* map — and a reader who
              never saw which source would not know where to undo it.

              On Add Source there is nothing to choose: the source is the one
              being created. Said rather than omitted, for the same reason — and
              because the map is saved with the source rather than now, which a
              reader has to know before pressing Create.
            */}
            {draft ? (
              <FieldSet
                label="Source"
                hint="The source you are adding. The group is saved with it when you create it."
              >
                <Badge>not saved yet</Badge>
              </FieldSet>
            ) : (
            <Field
              label="Source"
              hint="The group is made from this source's labels and saved on its profile."
              error={errorFor("profile")}
            >
              <Select
                value={profileId}
                onChange={(event) => setProfileId(event.target.value)}
                // The same hazard as the box below, minus the useful half:
                // Enter on a select inside a form submits it in some browsers,
                // and there is nothing here it should mean.
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    event.stopPropagation();
                  }
                }}
              >
                {candidates.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.name} — {candidate.league} {candidate.season}
                  </option>
                ))}
              </Select>
            </Field>
            )}

            <Field
              label="Group name"
              hint="What the Positions Groups Excluded dropdown will offer, and what the published files call the cohort."
              error={errorFor("group")}
            >
              <TextInput
                value={group}
                autoFocus
                placeholder="Special teams"
                aria-invalid={errorFor("group") ? true : undefined}
                onChange={(event) => {
                  setGroup(event.target.value);
                  setProblem(null);
                }}
                // **Enter adds the group; it must not reach the form outside.**
                // This dialog is opened from a control that both field-mapping
                // screens render *inside* `NewStatisticForm`'s `<form>`, and a
                // text box inside a form submits it on Enter however deeply it
                // is nested — so without this, typing a group name and pressing
                // Enter would create a half-configured statistic behind the
                // dialog.
                onKeyDown={(event) => {
                  if (event.key !== "Enter") return;
                  event.preventDefault();
                  event.stopPropagation();
                  submit();
                }}
              />
            </Field>

            <FieldSet
              label={draft ? "Labels in this source's payload" : "Labels this source publishes"}
              // Behind the label, like the other two explanations on this
              // screen: it says how the list works, which is wanted once, and
              // a permanent paragraph above every control is what makes a
              // dialog read as documentation.
              tip="Ticked labels become this group. The ones with no group are the positions currently falling to UNKNOWN. A label this source has not published yet can be added by hand below."
            >
              {/*
                **`!draft &&`, because a disabled query is pending for ever.**
                The labels query is switched off on Add Source — there is no
                profile to read a Run from — and react-query holds a query that
                never ran at `status: "pending"`, which is not "in flight" but
                "will never fly". Spinning on `labelsPending` alone left the
                draft's own labels, already in hand, behind a spinner that
                could not resolve. The same guard is on `usable` above.
              */}
              {!draft && labelsPending ? (
                <Spinner label="Reading the labels this source published" />
              ) : (
                <>
                  {rows.length === 0 && typedRows.length === 0 ? (
                    // **A banner, not a muted line.** With nothing published
                    // this is the whole reason the list is empty, which is a
                    // warning rather than an aside — and an aside in grey under
                    // an empty frame is what left somebody pressing a button
                    // that could never succeed.
                    //
                    // **Three absences, three remedies.** A source that has
                    // never run has sent nothing; one that ran before the portal
                    // began recording labels has sent plenty and recorded none
                    // of it; a draft may have no field bound as Position yet.
                    // Each now ends by naming the box below, because the absence
                    // is no longer a dead end.
                    draft ? (
                      <Banner tone="warning" title="No position labels were found in this payload">
                        A group is made from the values of the field you set to
                        Position. Set one and its labels will be here — or add
                        the label by hand below.
                      </Banner>
                    ) : published?.published_run ? (
                      <Banner
                        tone="warning"
                        title="This source last ran before the portal recorded position labels"
                      >
                        Run it again and the labels it publishes will be here, or
                        add the label by hand below.
                      </Banner>
                    ) : (
                      <Banner tone="warning" title="This source has not published a run yet">
                        There are no labels to read until it has. Run it once, or
                        add the label by hand below.
                      </Banner>
                    )
                  ) : (
                    <div className="flex max-h-[14rem] flex-col overflow-y-auto rounded-md border border-hairline">
                      {/* Hand-added first: they are the thing just done, and a
                          label typed into a box that then appears forty rows
                          down reads as having been swallowed. */}
                      {typedRows.map((row) => (
                        <label
                          key={`typed:${row.label}`}
                          className="flex cursor-pointer items-center gap-xs border-b border-hairline-soft px-sm py-xs text-caption last:border-b-0"
                        >
                          <Checkbox
                            checked={chosen.includes(row.label)}
                            onChange={() => {
                              setChosen((held) =>
                                held.includes(row.label)
                                  ? held.filter((label) => label !== row.label)
                                  : [...held, row.label],
                              );
                              setProblem(null);
                            }}
                          />
                          <span className="min-w-0 flex-1 truncate text-ink">{row.label}</span>
                          {/* Where the measured rows carry a count. Saying
                              "0 players" beside a label nobody has measured
                              would read as a finding about the publisher
                              rather than as the absence of one. */}
                          <Badge>added by hand</Badge>
                          <Badge tone={row.mapped ? undefined : "warning"}>
                            {row.canonical_position_group}
                          </Badge>
                        </label>
                      ))}
                      {rows.map((row) => (
                        <label
                          key={row.label || "(blank)"}
                          className="flex cursor-pointer items-center gap-xs border-b border-hairline-soft px-sm py-xs text-caption last:border-b-0"
                        >
                          <Checkbox
                            checked={chosen.includes(row.label)}
                            onChange={() => {
                              setChosen((held) =>
                                held.includes(row.label)
                                  ? held.filter((label) => label !== row.label)
                                  : [...held, row.label],
                              );
                              setProblem(null);
                            }}
                          />
                          <span className="min-w-0 flex-1 truncate text-ink">
                            {row.label || <Muted>(no position sent)</Muted>}
                          </span>
                          <span className="shrink-0 text-muted-soft">{row.players}</span>
                          <Badge tone={row.mapped ? undefined : "warning"}>
                            {row.canonical_position_group}
                          </Badge>
                        </label>
                      ))}
                    </div>
                  )}

                  {/*
                    **Outside the empty check, because the empty case is the one
                    it is for.** A source with no Run, or one whose Position
                    field nobody has bound yet, is exactly where somebody knows
                    the label and the portal does not.

                    Enter adds the label rather than the group: the box is for
                    one more label, and submitting the dialog from it would skip
                    the tick the operator came here to make. It must also not
                    reach the form outside — see the group name box above.
                  */}
                  <div className="mt-xs flex items-start gap-xs">
                    <TextInput
                      value={typing}
                      placeholder="ATH"
                      aria-label="Label this source has not published"
                      aria-invalid={errorFor("typed") ? true : undefined}
                      onChange={(event) => {
                        setTyping(event.target.value);
                        setProblem(null);
                      }}
                      onKeyDown={(event) => {
                        if (event.key !== "Enter") return;
                        event.preventDefault();
                        event.stopPropagation();
                        addTyped();
                      }}
                    />
                    <Button variant="secondary" onClick={addTyped} disabled={!typing.trim()}>
                      Add label
                    </Button>
                  </div>
                  <Muted>
                    For a label this source has not published yet — spelled the way
                    the publisher sends it. It maps nobody until a Run publishes it.
                  </Muted>
                  {errorFor("typed") && (
                    <span className="mt-xxs block text-caption text-danger-ink">
                      {errorFor("typed")}
                    </span>
                  )}
                </>
              )}
              {errorFor("labels") && (
                <span className="mt-xxs block text-caption text-danger-ink">
                  {errorFor("labels")}
                </span>
              )}
            </FieldSet>

            {/*
              What pressing the button does, in players. "Add a group" is
              otherwise a decision made against four letters and a tick.
            */}
            {chosen.length > 0 && (
              <Muted>
                {moving === 0
                  ? "The latest run published none of these labels, so this moves nobody today."
                  : `${moving} published ${moving === 1 ? "player" : "players"} would be ranked in this group from the next run.`}
              </Muted>
            )}

            {/*
              Taking a label out of a group it is already in is legitimate — it
              is why a profile's map overrides the sport's at all — and it
              changes which cohort those players are percentiled against. Said
              before the button rather than after.
            */}
            {taken.length > 0 && (
              <Banner tone="warning" title="This moves labels out of a group they are already in">
                <p className="m-0">
                  {taken.map((move) => `${move.label} (now ${move.from})`).join(", ")}. Those
                  players will be compared against a different cohort from the next run.
                </p>
              </Banner>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
